import { useState } from "react";
import { Loader2, KeyRound, AlertCircle, CheckCircle2, Eye, EyeOff } from "lucide-react";
import { authFetch } from "../lib/authFetch";
import { useClientAuth } from "../lib/auth-context";
import { useClientRouter } from "../lib/router-context";

function getPasswordStrengthError(password: string): string | null {
  if (password.length < 10) return "Le mot de passe doit contenir au moins 10 caractères.";
  if (!/\d/.test(password)) return "Le mot de passe doit contenir au moins 1 chiffre.";
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) return "Le mot de passe doit contenir au moins 1 caractère spécial.";
  return null;
}

export default function ChangePasswordPage() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const { refreshUser } = useClientAuth();
  const { navigate } = useClientRouter();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const strengthError = getPasswordStrengthError(newPassword);
    if (strengthError) { setError(strengthError); return; }
    if (newPassword !== confirmPassword) { setError("Les mots de passe ne correspondent pas."); return; }
    if (newPassword === currentPassword) { setError("Le nouveau mot de passe doit être différent du mot de passe actuel."); return; }

    setLoading(true);
    try {
      const res = await authFetch("/api/espace-client/auth/change-password", {
        method: "POST",
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message || "Erreur lors du changement de mot de passe."); return; }
      if (!data.token) throw new Error("Session renouvelée indisponible");
      localStorage.setItem("ec_token", data.token);
      setSuccess(true);
      await refreshUser();
      setTimeout(() => navigate("/"), 1500);
    } catch {
      setError("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{
      background: "linear-gradient(135deg, #4e63f0 0%, #2facf3 100%)",
    }}>
      <div className="w-full max-w-md space-y-8">
        <div className="flex flex-col items-center text-center space-y-3">
          <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center shadow-lg">
            <KeyRound className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Créer votre mot de passe</h1>
            <p className="text-sm text-white/70 mt-1">Vous devez définir un mot de passe personnel avant d'accéder à votre espace.</p>
          </div>
        </div>

        <div className="bg-card border border-border/50 rounded-2xl shadow-2xl overflow-hidden">
          {success ? (
            <div className="p-8 flex flex-col items-center gap-4 text-center">
              <CheckCircle2 className="w-12 h-12 text-green-500" />
              <div>
                <p className="font-semibold text-foreground">Mot de passe modifié !</p>
                <p className="text-sm text-muted-foreground mt-1">Redirection vers votre tableau de bord…</p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div>
                <h2 className="font-semibold text-lg text-foreground">Changement de mot de passe</h2>
                <p className="text-sm text-muted-foreground mt-1">Minimum 10 caractères, au moins 1 chiffre et 1 caractère spécial.</p>
              </div>

              {error && (
                <div className="flex items-start gap-2 bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2.5">
                  <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}

              {[
                { id: "current-password", label: "Mot de passe actuel (temporaire)", value: currentPassword, setter: setCurrentPassword, show: showCurrent, toggle: () => setShowCurrent(v => !v), testId: "input-current-password" },
                { id: "new-password", label: "Nouveau mot de passe", value: newPassword, setter: setNewPassword, show: showNew, toggle: () => setShowNew(v => !v), testId: "input-new-password" },
                { id: "confirm-password", label: "Confirmer le nouveau mot de passe", value: confirmPassword, setter: setConfirmPassword, show: showNew, toggle: () => setShowNew(v => !v), testId: "input-confirm-password" },
              ].map(field => (
                <div key={field.id} className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground" htmlFor={field.id}>{field.label}</label>
                  <div className="relative">
                    <input
                      id={field.id}
                      type={field.show ? "text" : "password"}
                      value={field.value}
                      onChange={e => field.setter(e.target.value)}
                      required
                      className="w-full h-11 px-3 pr-10 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                      data-testid={field.testId}
                    />
                    <button type="button" onClick={field.toggle} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {field.show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              ))}

              <button
                type="submit"
                disabled={loading}
                className="w-full h-11 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60 text-white shadow-lg"
                style={{ background: "linear-gradient(135deg, #4e63f0 0%, #2facf3 100%)" }}
                data-testid="button-change-password"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                Définir mon mot de passe
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
